import { useMemo } from 'react';
import { TextField, NumberField, SelectInput, usePermissions, FunctionField, useResourceContext, useTranslate } from 'react-admin';
import { useTheme, useMediaQuery, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import { formatIntervalSince } from '@/helpers/date/formatTimeInterval.js';
import { SearchBar } from '@/components/global/list/searchBar';
import TransactionStatisticList from '@/components/analytics/statisticList.jsx';
import { PeriodFilter, ReferenceArrayFilter } from '@/components/global/filters/filters.jsx';
import { TagReferenceArrayFilter } from '@/components/global/filters/TagReferenceArrayFilter.jsx';
import { useDrawer } from '@/composables/useDrawer';
import TableHeaderCellWithSubtitle from '@/components/global/fields/TableHeaderCellWithSubtitle.jsx';
import FromToFilterComponent from '@/components/global/filters/FromToFilterComponent.jsx';
import ListCollectionPointField from '@/components/collectionPoint/ListCollectionPointField.jsx';
import TransactionPayoutField from '@/components/transaction/TransactionPayoutField.jsx';
import ContractorField from '@/components/contractors/ContractorField.jsx';
import { TransactionDetailsContent } from '@/components/transaction/TransactionDetailsContent.jsx';
import TransactionAppColumnField from '@/components/transaction/TransactionAppColumnField.jsx';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import CustomDatagridHeader from '@/components/global/list/CustomDatagridHeader.jsx';
import { transactionStatusChoices, operatorStatusChoices, operatorFilterChoices } from '@/components/global/options/selectOptions.jsx';
import CustomSelectArrayInput from '@/components/global/inputs/CustomSelectArrayInput.jsx';
import BottleAcceptedIcon from '@/assets/icons/bottle-accepted-icon.svg?react';
import BottleRejectedIcon from '@/assets/icons/bottle-rejected-icon.svg?react';
import { StyledTransactionList } from '@/components/transaction/list/StyledTransactionList.jsx';
import { ContainerSumWithIcon } from '@/components/transaction/fields/ContainerSumWithIcon.jsx';
import { TransactionListActions } from '@/components/transaction/buttons/TransactionListActions.jsx';
import { TransactionBulkActionButtons } from '@/components/transaction/buttons/TransactionBulkActions.jsx';
import { TransactionMoreActions } from '@/components/transaction/buttons/TransactionMoreActions.jsx';
import { getDeviceModelFilterOptionText, getDeviceModelSearchFilter } from '@/helpers/parameter/parameterSubsetDisplay.js';

export const TransactionList = () => {
    const resource = useResourceContext();
    const { openDrawer } = useDrawer();
    const isTest = 'test-transaction' === resource;
    const { isLoading, permissions } = usePermissions();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
    const translate = useTranslate();

    const hasContractorField = permissions.isAdmin || permissions.isBok || permissions.isPosOwner || permissions.isService;
    const showOperatorField = permissions.isAdmin || permissions.isBok || permissions.isService;

    const filters = useMemo(
        () => [
            <SearchBar key='search' source='value' label={translate('common.search')} alwaysOn />,
            <PeriodFilter key='period' alwaysOn={true} />,
            <FromToFilterComponent
                key='containersSum'
                source='containersSum_range'
                label={translate('resources.transaction.labels.packagesCount')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <FromToFilterComponent
                key='validContainersSum'
                source='validContainersSum_range'
                label={translate('resources.transaction.labels.validPackagesCount')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            hasContractorField && (
                <ReferenceArrayFilter
                    key='collectionPoint->contractor->id_in'
                    source='collectionPoint->contractor->id_in'
                    label={translate('common.contractor')}
                    reference='contractor'
                    forceDesktopOnMobile={true}
                    alwaysOn={isMobile}
                />
            ),
            <ReferenceArrayFilter
                key='collectionPoint->id_in'
                source='collectionPoint->id_in'
                label={translate('common.collectionPoint')}
                reference='collection-point'
                optionText={choice => choice.customId}
                searchFields={['customId']}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <ReferenceArrayFilter
                key='device->group->id_in'
                source='device->group->id_in'
                optionText={choice => choice.name}
                label={translate('common.deviceGroup')}
                reference='device-group'
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <ReferenceArrayFilter
                key='device->modelId_in'
                source='device->modelId_in'
                reference='device-model'
                label={translate('common.model')}
                optionText={getDeviceModelFilterOptionText}
                getSearchFilter={getDeviceModelSearchFilter}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <ReferenceArrayFilter
                key='device->id_in'
                source='device->id_in'
                reference='device'
                label={translate('common.device')}
                optionText={choice => choice?.serialNumber || ''}
                searchFields={['serialNumber']}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <ReferenceArrayFilter
                key='bagId_in'
                source='bagId_in'
                label={translate('common.bag')}
                reference='bag'
                optionText={choice => (choice.seal ? choice.seal : choice.device?.serialNumber)}
                searchFields={['seal', 'device.serialNumber']}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <ReferenceArrayFilter
                key='transactionContainers->product->id_in'
                source='transactionContainers->product->id_in'
                label={translate('resources.transaction.labels.productFilter')}
                reference='product'
                filter={{ status: 1 }}
                optionText={choice => choice.ean}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <TagReferenceArrayFilter
                key='transactionContainers->product->tags->id_in'
                source='transactionContainers->product->tags->id_in'
                optionText={choice => choice.name}
                label={translate('resources.transaction.labels.productTags')}
                reference='tag'
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <SelectInput
                key='status'
                source='status'
                label={translate('common.status')}
                choices={transactionStatusChoices(translate).map(choice => ({ id: choice.id, name: choice.name }))}
                emptyText={translate('common.all')}
            />,
            <SelectInput
                key='operatorStatus'
                source='operatorStatus'
                label={translate('resources.transaction.fields.operatorStatus')}
                choices={operatorStatusChoices(translate).map(choice => ({ id: choice.id, name: choice.name }))}
                emptyText={translate('common.all')}
            />,
            showOperatorField && (
                <CustomSelectArrayInput
                    key='collectionPoint->operator_in'
                    source='collectionPoint->operator_in'
                    label={translate('common.operator')}
                    choices={operatorFilterChoices(translate)}
                    alwaysOn={isMobile}
                    fullWidth
                    alwaysDropdown={true}
                />
            ),
            <FromToFilterComponent
                key='transactionTime_time'
                source='transactionTime_time_range'
                label={translate('common.filters.hours')}
                type='time'
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
        ],
        [isMobile, hasContractorField, showOperatorField, translate],
    ).filter(Boolean);

    if (isLoading) return null;

    return (
        <StyledTransactionList
            filters={filters}
            actions={<TransactionListActions />}
            sort={{ field: 'createdAt', order: 'DESC' }}
            filter={{ isTest, withAggregates: true }}
            addLastColumnShadow={isDesktop && permissions.isAdmin && !isTest}
        >
            <>
                {isTest || <TransactionStatisticList />}
                <CustomDatagridConfigurable
                    header={<CustomDatagridHeader />}
                    expandSingle
                    rowClick='expand'
                    expand={<TransactionDetailsContent />}
                    bulkActionButtons={<TransactionBulkActionButtons />}
                    className='responsive-table'
                    sx={{
                        '& .RaDatagrid-expandIconCell, & .RaDatagrid-headerCell:first-of-type': {
                            display: 'none',
                        },
                        '& .MuiTableCell-root:first-of-type': {
                            padding: '0px',
                        },
                    }}
                >
                    {hasContractorField && (
                        <FunctionField
                            source='contractor'
                            label={translate('common.contractor')}
                            sortBy='collectionPoint.contractor.name'
                            render={record => (
                                <ContractorField contractor={record?.collectionPoint?.contractor} filterKey='collectionPoint->contractor->id_in' />
                            )}
                        />
                    )}
                    <FunctionField
                        source='collectionPoint.id'
                        label={translate('common.collectionPoint')}
                        sortBy='collectionPoint.customId'
                        render={record => (
                            <ListCollectionPointField
                                collectionPoint={record.collectionPoint}
                                sx={{ cursor: 'pointer' }}
                                onClick={e => {
                                    e.stopPropagation();
                                    openDrawer(record.collectionPoint.id, 'collection-point-show');
                                }}
                                showShopName
                            />
                        )}
                    />
                    <FunctionField
                        source='transactionTime'
                        sortBy='transactionTime'
                        label={
                            <TableHeaderCellWithSubtitle
                                title={translate('resources.transaction.fields.transactionTime')}
                                subTitle={translate('resources.transaction.fields.transactionTimeSubtitle')}
                            />
                        }
                        render={record =>
                            record.transactionTime ? (
                                <Tooltip
                                    title={
                                        formatIntervalSince(record.transactionTime)
                                            ? `${formatIntervalSince(record.transactionTime)} ${translate('notification.timeAgo')}`
                                            : ''
                                    }
                                    arrow
                                    classes={{ tooltip: 'tooltip-black' }}
                                >
                                    <span>{format(new Date(record.transactionTime), 'dd.MM.yyyy HH:mm')}</span>
                                </Tooltip>
                            ) : (
                                ''
                            )
                        }
                    />
                    <FunctionField
                        cellClassName='transaction-metrics-cell'
                        source='validContainersSum'
                        sortBy='validContainersSum'
                        label={translate('resources.bag.fields.containerCount')}
                        render={record => <ContainerSumWithIcon record={record} source='validContainersSum' icon={BottleAcceptedIcon} />}
                    />
                    <FunctionField
                        cellClassName='transaction-metrics-cell'
                        source='invalidContainersSum'
                        sortBy='invalidContainersSum'
                        label={translate('resources.transaction.fields.invalidContainersSum')}
                        render={record => <ContainerSumWithIcon record={record} source='invalidContainersSum' icon={BottleRejectedIcon} />}
                    />
                    <NumberField
                        cellClassName='transaction-metrics-cell'
                        source='payout.detailsCash'
                        sortBy='payout.detailsCash'
                        label={translate('resources.transaction.fields.payoutValue')}
                        options={{ style: 'currency', currency: 'PLN' }}
                    />
                    <FunctionField
                        cellClassName='transaction-metrics-cell'
                        source='userId'
                        sortBy='userId'
                        label={translate('resources.transaction.fields.userId')}
                        render={record => <TransactionAppColumnField record={record} />}
                    />
                    <FunctionField
                        cellClassName='transaction-metrics-cell'
                        source='payout.type'
                        sortBy='payout.type'
                        label={translate('resources.transaction.fields.payoutType')}
                        render={record => <TransactionPayoutField record={record} />}
                    />
                    <FunctionField
                        source='device.serialNumber'
                        label={translate('common.deviceSerialNumberShort')}
                        sortBy='device.serialNumber'
                        render={record => (
                            <TextField
                                source='device.serialNumber'
                                sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                onClick={e => {
                                    e.stopPropagation();
                                    if (record.collectionPoint) {
                                        openDrawer(record.device.id, 'device-show');
                                    }
                                }}
                            />
                        )}
                    />
                    {permissions.isAdmin && !isTest && (
                        <FunctionField
                            className='options-field'
                            cellClassName='options-field'
                            source='forwardingHistory'
                            label={translate('common.options')}
                            sortable={false}
                            onClick={event => event.stopPropagation()}
                            render={() => <TransactionMoreActions />}
                        />
                    )}
                </CustomDatagridConfigurable>
            </>
        </StyledTransactionList>
    );
};
